/**
 * `Intl.PluralRules` arrived in Safari 13 (iOS 13), and core-js — what
 * `react-app-polyfill/stable` pulls in — deliberately does not cover ECMA-402,
 * so on the older WebViews inside this app's browserslist floor it is simply
 * absent.
 *
 * react-intl needs it for every ICU plural message (`{count, plural, …}`).
 * Without it formatting throws, react-intl reports FORMAT_ERROR and falls back
 * to rendering the message id, so users see "SearchFiltersMobile.showListings"
 * where a button label belongs (VANSITE-FRONTEND-4R9 / -4R8 — the two ids that
 * happened to be reported, but the failure hits every plural message in the
 * app).
 *
 * The polyfill is loaded lazily and only where `Intl.PluralRules` is missing or
 * broken, so modern engines download nothing — the polyfill and the locale data
 * for all seven marketplace locales share one lazy chunk (~20 kB gzipped) that
 * only legacy engines ever fetch. Keeping all locales in the one chunk makes
 * this independent of locale resolution and of later locale switches.
 *
 * VERSION PIN — `@formatjs/intl-pluralrules` must stay on 5.x. Do NOT bump to
 * 6.x. From 6.0 the polyfill computes through `@formatjs/bigdecimal`, whose
 * published build contains BigInt literals (`10n`). Babel cannot down-compile
 * BigInt syntax, so the chunk would be a SyntaxError on exactly the engines that
 * fetch it (Safari/WKWebView < 13, which have no BigInt either): the chunk fails
 * to parse, its `import()` rejects with a ChunkLoadError, and the users this
 * polyfill exists for get a blank page instead of a degraded one. 5.x computes
 * with `decimal.js` and stays parseable. `intlPluralRules.test.js` scans the
 * installed tree for BigInt syntax and fails if it reappears — also through a
 * transitive bump.
 */
import { logError } from '../log';

// Must mirror `config.languages` (src/config.js). Webpack needs static import
// specifiers, so this list cannot be derived from the config at runtime — the
// test asserts the two sets are identical.
const loadLocaleData = () =>
  Promise.all([
    import(/* webpackChunkName: "intl-pluralrules" */ '@formatjs/intl-pluralrules/locale-data/de.js'),
    import(/* webpackChunkName: "intl-pluralrules" */ '@formatjs/intl-pluralrules/locale-data/en.js'),
    import(/* webpackChunkName: "intl-pluralrules" */ '@formatjs/intl-pluralrules/locale-data/es.js'),
    import(/* webpackChunkName: "intl-pluralrules" */ '@formatjs/intl-pluralrules/locale-data/fr.js'),
    import(/* webpackChunkName: "intl-pluralrules" */ '@formatjs/intl-pluralrules/locale-data/it.js'),
    import(/* webpackChunkName: "intl-pluralrules" */ '@formatjs/intl-pluralrules/locale-data/no.js'),
    import(/* webpackChunkName: "intl-pluralrules" */ '@formatjs/intl-pluralrules/locale-data/sv.js'),
  ]);

/**
 * Mirrors the check in `@formatjs/intl-pluralrules/should-polyfill`: a missing
 * constructor, or one that exists but ignores fraction-digit options — early
 * WebKit answers 'one' for `new Intl.PluralRules('en', { minimumFractionDigits:
 * 2 }).select(1)` where the spec says 'other'.
 *
 * `should-polyfill` is deliberately not imported — and not only for weight
 * (it drags `@formatjs/intl-localematcher` plus the generated supported-locales
 * list into the main bundle to answer a question two expressions settle): its
 * locale matcher needs `Intl.Locale`, which the engines probed here do not have.
 * This check is therefore the only gate; the install below skips the package's
 * own (see `installIntlPluralRulesPolyfill`).
 *
 * @returns {boolean} true when the polyfill chunk has to be fetched
 */
export const needsIntlPluralRulesPolyfill = () => {
  // `typeof Intl` only guards against a ReferenceError; every engine above the
  // browserslist floor has the Intl object — what is absent or broken is the
  // ECMA-402 part of it.
  if (typeof Intl === 'undefined' || typeof Intl.PluralRules !== 'function') {
    return true;
  }

  try {
    return new Intl.PluralRules('en', { minimumFractionDigits: 2 }).select(1) === 'one';
  } catch {
    // An implementation that throws on a legal option set is broken too.
    return true;
  }
};

/**
 * Pins every `new Intl.PluralRules(...)` of the freshly installed polyfill to
 * the `lookup` locale matcher.
 *
 * Without this the install lands but stays unusable: the polyfill's default
 * matcher ('best fit') resolves locales through `new Intl.Locale(tag).maximize()`,
 * which the engines that need the polyfill do not have — so the first plural
 * message would throw the very TypeError this module exists to prevent.
 *
 * `lookup` is pure string matching (RFC 4647) and resolves our seven
 * marketplace tags identically; a regional tag falls back by truncation
 * ('de-DE' -> 'de'), which is what best fit would pick here too.
 *
 * The wrapper keeps the polyfill's statics reachable — the locale-data modules
 * call `Intl.PluralRules.__addLocaleData(...)` and react-intl may call
 * `supportedLocalesOf` — by inheriting from the installed constructor.
 */
const pinToLookupMatcher = () => {
  const Polyfilled = Intl.PluralRules;

  // Not an arrow function: this is constructed with `new`.
  const PluralRulesWithLookupMatcher = function PluralRules(locales, options) {
    return new Polyfilled(locales, { ...options, localeMatcher: 'lookup' });
  };
  PluralRulesWithLookupMatcher.prototype = Polyfilled.prototype;
  Object.setPrototypeOf(PluralRulesWithLookupMatcher, Polyfilled);

  Object.defineProperty(Intl, 'PluralRules', {
    value: PluralRulesWithLookupMatcher,
    writable: true,
    enumerable: false,
    configurable: true,
  });
};

/**
 * Installs `Intl.PluralRules` on engines that lack (or botch) it. Resolves once
 * plural formatting is safe to use, so call sites must await it before the first
 * render.
 *
 * Never rejects: a failed chunk load (404 after a deploy, adblocker, parse
 * error) must degrade plural messages, never block the mount — this is awaited
 * before `hydrateRoot`.
 *
 * @returns {Promise<void>}
 */
const installIntlPluralRulesPolyfill = async () => {
  if (!needsIntlPluralRulesPolyfill()) {
    return;
  }

  try {
    // `polyfill.js` is deliberately NOT the entry point here: it gates the
    // install behind its own `shouldPolyfill()`, which routes through
    // `@formatjs/intl-localematcher` — and that matcher calls
    // `new Intl.Locale(...)`. `Intl.Locale` landed in Safari 14, one release
    // later than `Intl.PluralRules` (Safari 13), so on exactly the engines this
    // polyfill exists for the install threw before it began: `Intl.PluralRules`
    // never landed and every plural message kept rendering its raw message id
    // (VANSITE-FRONTEND-4RJ -> -4RK). `polyfill-force.js` skips that check,
    // which is sound because `needsIntlPluralRulesPolyfill()` above already ran
    // the same capability probe without touching the locale matcher.
    await import(/* webpackChunkName: "intl-pluralrules" */ '@formatjs/intl-pluralrules/polyfill-force.js');
    pinToLookupMatcher();
    // Sequential on purpose, and not foldable into a Promise.all: every
    // locale-data module calls `Intl.PluralRules.__addLocaleData(...)` at module
    // evaluation time and does nothing at all when the polyfill is not installed
    // yet, so evaluating it first loses the data silently. Both sides live in the
    // same `intl-pluralrules` chunk, so the second await costs no extra network
    // roundtrip.
    await loadLocaleData();
  } catch (e) {
    logError(e, 'intl-pluralrules-polyfill-failed');
  }
};

export default installIntlPluralRulesPolyfill;
